/**
 * MessageBubble.jsx — renders a single message row in the chat transcript.
 *
 * Extracted from chat.jsx to keep the parent screen focused on orchestration
 * (state, streaming, send/receive) rather than the ~380 lines of per-message
 * layout: attachments, generated media (image/video/sound), verify chips,
 * compute cards, Canvas artifact chips, feedback + copy actions.
 *
 * Callback props are all optional so this component works in read-only
 * contexts (e.g. shared conversations) too — buttons simply won't render.
 */
import { useState, useRef, useEffect } from "react";
import * as L from "lucide-react";
import { WeatherCard } from "@/components/chat/WeatherCard";
import { toast } from "sonner";
import api from "@/lib/api";

import { AssistantBody, CopyReplyButton } from "@/components/chat/MessageBody";
import VideoWaiting from "@/components/chat/VideoWaiting";
import MessageFeedback from "@/components/MessageFeedback";
import { ArtifactBuildingCard, ArtifactInterruptedCard, ArtifactReadyCard } from "@/components/chat/ArtifactCard";
import { LanguageSuggestionChip } from "@/components/chat/LanguageSuggestionChip";
import SourceBadge from "@/components/SourceBadge";
import { LiveActivityStrip } from "@/components/chat/LiveActivityStrip";
import { useShowSources, useShowConfidence } from "@/hooks/useChatPrefs";
import { splitByArtifacts } from "@/lib/artifact-segments";
import { isUncertainReply } from "@/lib/uncertainty";

/** "Send to Studio" — turns a reply into a narratable Studio draft, split
 *  into paragraphs, then jumps to the Studio screen with it open. */
function SendToStudioButton({ text, testid }) {
  const [busy, setBusy] = useState(false);
  const clean = (text || "").trim();
  if (clean.length < 40) return null;   // too short to be worth narrating

  const send = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/studio/from-text", { text: clean.slice(0, 20000) });
      toast.success(`Studio draft ready — ${data.blocks?.length || 0} paragraph${data.blocks?.length === 1 ? "" : "s"}`);
      try { sessionStorage.setItem("rammy-studio-open", data.id); } catch (_) { /* ignore */ }
      window.dispatchEvent(new CustomEvent("rammy-open-studio-project", { detail: data.id }));
      window.dispatchEvent(new CustomEvent("rammy-navigate", { detail: { screen: "studio" } }));
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't send that to Studio");
    } finally {
      setBusy(false);
    }
  };

  return (
    <button
      type="button"
      onClick={send}
      disabled={busy}
      className="text-[11px] inline-flex items-center gap-1 text-[var(--rammy-text-muted)] hover:text-[var(--rammy-cyan-dark)] transition-colors disabled:opacity-50"
      data-testid={testid}
      title="Turn this into narrated audio in Studio"
    >
      {busy ? <L.Loader2 size={11} className="animate-spin"/> : <L.AudioLines size={11}/>} Send to Studio
    </button>
  );
}

/** Strip internal directives from the visible assistant text.
 *  Also strips the <rammy:style> regeneration marker so it never appears in
 *  either the user's own pill or the assistant's reply preview. */
function cleanContent(content) {
  return (content || "")
    .replace(/<execute>[\s\S]*?<\/execute>/gi, "")
    // Match `<generate ...>` including quoted attribute values that may
    // contain `>` (e.g. Gmail msgid="<abc@x>").
    .replace(/<generate\s+(?:[^"/>]|"[^"]*")*?\s*\/>/gi, "")
    // Content-gate marker for high-stakes drafts (legal/medical/financial).
    // Stripped from the visible reply; a separate banner surfaces the gate.
    .replace(/<rammy:content-gate\s+(?:[^"/>]|"[^"]*")*?\s*\/>/gi, "")
    .replace(/<rammy:style>[\s\S]*?<\/rammy:style>/gi, "")
    .trim();
}

/** Detect a legal / high-stakes content gate marker in the raw assistant
 *  reply. Returns { kind, jurisdiction, reason } or null. */
function detectContentGate(content) {
  const m = (content || "").match(
    /<rammy:content-gate\s+((?:[^"/>]|"[^"]*")*?)\s*\/>/i
  );
  if (!m) return null;
  const attrs = m[1] || "";
  const grab = (name) => {
    const r = new RegExp(`${name}\\s*=\\s*"([^"]*)"`, "i").exec(attrs);
    return r ? r[1] : "";
  };
  return {
    kind: grab("kind") || "legal",
    jurisdiction: grab("jurisdiction") || "your jurisdiction",
    reason: grab("reason") || "This is a high-stakes draft — get it reviewed by a licensed professional.",
  };
}

/** Format an ISO timestamp as a warm, human "when" string, e.g.
 *  "Today at 5:00 pm", "Tomorrow at 9:30 am", "Sun, Feb 16 · 5:00 pm".
 *  Uses the user's local timezone via toLocaleTimeString/toDateString. */
function formatReminderWhen(iso) {
  if (!iso) return "";
  const when = new Date(iso);
  if (Number.isNaN(when.getTime())) return iso;
  const time = when.toLocaleTimeString(undefined, { hour: "numeric", minute: "2-digit" });
  const today = new Date();
  const tomorrow = new Date(); tomorrow.setDate(today.getDate() + 1);
  const sameDay = (a, b) =>
    a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();
  if (sameDay(when, today))    return `Today at ${time}`;
  if (sameDay(when, tomorrow)) return `Tomorrow at ${time}`;
  const dayPart = when.toLocaleDateString(undefined, { weekday: "short", month: "short", day: "numeric" });
  return `${dayPart} · ${time}`;
}

/** Inline confirmation card rendered after Rammy auto-creates a reminder from
 *  a `<generate kind="reminder"/>` tag. Read-only — the user can navigate to
 *  the Reminders screen to edit or delete. */
function ReminderCard({ reminder, testid }) {
  const repeatBadge = reminder.repeat && reminder.repeat !== "none" ? reminder.repeat : null;
  const openReminders = () => {
    // Fire a custom event AppShell listens for; falls back to navigation.
    try { window.dispatchEvent(new CustomEvent("rammy-navigate", { detail: { screen: "reminders" } })); } catch (_) {}
  };
  return (
    <div
      className="mt-1 mb-2 rounded-2xl border border-[var(--rammy-cyan)]/35 bg-gradient-to-br from-[var(--rammy-cyan)]/12 to-white p-3 w-[320px] max-w-full"
      data-testid={testid}
    >
      <div className="flex items-center gap-2 text-[10px] uppercase tracking-widest font-semibold text-[var(--rammy-cyan-dark)] mb-1">
        <L.BellRing size={11}/> Reminder set
        {repeatBadge && (
          <span className="text-[9px] text-[var(--rammy-text-muted)] normal-case tracking-normal font-normal">
            · repeats {repeatBadge}
          </span>
        )}
      </div>
      <div className="text-[15px] text-[var(--rammy-text)] font-medium leading-snug" data-testid={`${testid}-title`}>
        {reminder.title}
      </div>
      <div className="mt-1 text-[12px] text-[var(--rammy-text-sec)]" data-testid={`${testid}-when`}>
        {formatReminderWhen(reminder.when)}
      </div>
      {reminder.note && (
        <div className="mt-1.5 text-[12px] text-[var(--rammy-text-muted)] leading-relaxed">
          {reminder.note}
        </div>
      )}
      <button
        type="button"
        onClick={openReminders}
        data-testid={`${testid}-open`}
        className="mt-2.5 inline-flex items-center gap-1 text-[11px] font-semibold uppercase tracking-widest text-[var(--rammy-cyan-dark)] hover:text-[var(--rammy-text)] transition"
      >
        View in Reminders <L.ArrowRight size={11}/>
      </button>
    </div>
  );
}

/** Small popover menu attached to the Regenerate pill. Lets the user
 *  choose HOW they want the answer reshaped ("Same again", "Shorter",
 *  "Warmer", "More detail", "Cite sources") — the choice is passed back
 *  to the parent as a hidden hint string that gets appended to the
 *  original user message as `<rammy:style>{hint}</rammy:style>`. */
const REGEN_STYLES = [
  { key: null,           label: "Same again",   hint: "Try a different angle or wording."  },
  { key: "shorter",      label: "Shorter",      icon: "AlignLeft"                          },
  { key: "warmer",       label: "Warmer",       icon: "Heart"                              },
  { key: "more_detail",  label: "More detail",  icon: "ListChecks"                         },
  { key: "cite_sources", label: "Cite sources", icon: "Search"                             },
];

/** Inline card after Rammy drops a reply into the user's Gmail Drafts.
 *
 * Renders To / Subject / body preview and two actions:
 *  1. Send — POSTs to /api/gmail/send-reply (uses the ids Rammy already captured).
 *  2. Edit in Gmail — opens the draft directly in mail.google.com so the user
 *     can tweak wording before sending.
 *
 * Once sent, we swap the card into a "sent" state so accidental double-sends
 * are impossible. Read-only when `draft.sent` is truthy. */
/** Rammy actually booked the time. Undo stays available for an hour — the
 *  one honest way to give an agent write access without it feeling risky. */
function CalendarEventCard({ event, testid }) {
  const [cancelled, setCancelled] = useState(false);
  const [busy, setBusy] = useState(false);
  const undo = async () => {
    setBusy(true);
    try {
      await api.delete(`/calendar/events/${event.id}`);
      setCancelled(true);
      toast.success("Taken off your calendar");
    } catch { toast.error("Couldn't undo that — open your calendar to remove it."); }
    finally { setBusy(false); }
  };
  return (
    <div
      className="mt-1 mb-2 rounded-2xl border border-[var(--rammy-cyan)]/35 bg-gradient-to-br from-[var(--rammy-cyan)]/12 to-white p-3 w-[320px] max-w-full"
      data-testid={testid}
    >
      <div className="flex items-center gap-2 text-[10px] uppercase tracking-widest font-semibold text-[var(--rammy-cyan-dark)] mb-1">
        <L.CalendarPlus size={11}/> {cancelled ? "Removed" : "On your calendar"}
      </div>
      <div className={`text-[15px] font-medium leading-snug ${cancelled ? "line-through text-[var(--rammy-text-muted)]" : "text-[var(--rammy-text)]"}`}
        data-testid={`${testid}-title`}>
        {event.title}
      </div>
      <div className="mt-1 text-[12px] text-[var(--rammy-text-sec)]" data-testid={`${testid}-when`}>
        {formatReminderWhen(event.start)}
      </div>
      {!cancelled && (
        <div className="mt-2.5 flex items-center gap-3">
          <button
            type="button"
            onClick={undo}
            disabled={busy}
            data-testid={`${testid}-undo`}
            className="text-[11px] font-semibold uppercase tracking-widest text-[var(--rammy-text-muted)] hover:text-red-600 transition disabled:opacity-50"
          >
            Undo
          </button>
          {event.html_link && (
            <a href={event.html_link} target="_blank" rel="noreferrer"
              data-testid={`${testid}-open`}
              className="text-[11px] font-semibold uppercase tracking-widest text-[var(--rammy-cyan-dark)] hover:text-[var(--rammy-text)] transition">
              Open
            </a>
          )}
        </div>
      )}
    </div>
  );
}

/** A promise Rammy heard and is now holding. */
function PromiseCard({ promise, testid }) {
  const openHome = () => {
    try { window.dispatchEvent(new CustomEvent("rammy-navigate", { detail: { screen: "home" } })); } catch (_) {}
  };
  return (
    <div
      className="mt-1 mb-2 rounded-2xl border border-[var(--rammy-border)] bg-[var(--rm-surface)] p-3 w-[320px] max-w-full"
      data-testid={testid}
    >
      <div className="flex items-center gap-2 text-[10px] uppercase tracking-widest font-semibold text-[var(--rammy-text-muted)] mb-1">
        <L.Handshake size={11}/> I&rsquo;ll hold you to it
      </div>
      <div className="text-[15px] text-[var(--rammy-text)] font-medium leading-snug" data-testid={`${testid}-title`}>
        {promise.title}
      </div>
      <div className="mt-1 text-[12px] text-[var(--rammy-text-sec)]" data-testid={`${testid}-when`}>
        {promise.counterparty?.name ? `for ${promise.counterparty.name} · ` : ""}
        {formatReminderWhen(promise.due_at)}
      </div>
      <button
        type="button"
        onClick={openHome}
        data-testid={`${testid}-open`}
        className="mt-2.5 inline-flex items-center gap-1 text-[11px] font-semibold uppercase tracking-widest text-[var(--rammy-text-muted)] hover:text-[var(--rammy-text)] transition"
      >
        See it on Home <L.ArrowRight size={11}/>
      </button>
    </div>
  );
}

function GmailDraftCard({ draft, testid, onSent }) {
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(!!draft.sent);

  const send = async () => {
    if (sending || sent) return;
    setSending(true);
    try {
      await api.post("/gmail/send-reply", {
        message_id: draft.msgid || "",
        in_reply_to_msgid: draft.msgid || "",
        thread_id: draft.thread || "",
        to_email: draft.to,
        subject: draft.subject,
        body: draft.body,
      });
      setSent(true);
      onSent?.();
      toast.success("Sent");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Send failed. Open in Gmail?");
    } finally {
      setSending(false);
    }
  };

  return (
    <div
      className="mt-1 mb-2 rounded-2xl border border-[var(--rammy-cyan)]/35 bg-gradient-to-br from-white to-[var(--rammy-cyan)]/8 overflow-hidden w-full max-w-[520px]"
      data-testid={testid}
    >
      <div className="px-3.5 py-2 border-b border-[var(--rammy-cyan)]/25 flex items-center gap-2">
        <L.Mail size={13} className="text-[var(--rammy-cyan-dark)]"/>
        <span className="text-[10px] uppercase tracking-widest font-semibold text-[var(--rammy-cyan-dark)]">
          {sent ? "Sent" : "Draft ready"}
        </span>
        <span className="text-[10px] text-[var(--rammy-text-muted)] normal-case tracking-normal">
          {sent ? "Reply delivered" : "Saved in Gmail Drafts — review before sending"}
        </span>
      </div>

      <div className="p-3.5 space-y-1 text-[13px]" data-testid={`${testid}-preview`}>
        <div className="flex items-baseline gap-1.5">
          <span className="text-[11px] uppercase tracking-widest text-[var(--rammy-text-muted)] w-14 shrink-0">To</span>
          <span className="font-mono text-[12px] truncate flex-1" data-testid={`${testid}-to`}>{draft.to}</span>
        </div>
        <div className="flex items-baseline gap-1.5">
          <span className="text-[11px] uppercase tracking-widest text-[var(--rammy-text-muted)] w-14 shrink-0">Subject</span>
          <span className="font-medium truncate flex-1" data-testid={`${testid}-subject`}>
            {draft.subject?.toLowerCase().startsWith("re:") ? draft.subject : `Re: ${draft.subject || "(no subject)"}`}
          </span>
        </div>
        <div className="mt-2 pt-2 border-t border-[var(--rammy-cyan)]/15 text-[13px] whitespace-pre-wrap leading-relaxed text-[var(--rammy-text)] max-h-[240px] overflow-y-auto subtle-scroll" data-testid={`${testid}-body`}>
          {draft.body}
        </div>
      </div>

      <div className="px-3.5 py-2 bg-[var(--rammy-cyan)]/10 border-t border-[var(--rammy-cyan)]/25 flex items-center gap-2">
        {!sent && (
          <button
            type="button"
            onClick={send}
            disabled={sending}
            data-testid={`${testid}-send`}
            className="inline-flex items-center gap-1.5 h-8 px-3 rounded-full bg-[var(--rammy-cyan-dark)] text-white text-[12px] font-semibold hover:opacity-90 transition disabled:opacity-60"
          >
            {sending ? <L.Loader2 size={12} className="animate-spin"/> : <L.Send size={12}/>}
            {sending ? "Sending…" : "Send"}
          </button>
        )}
        <a
          href={draft.gmail_open_url || "https://mail.google.com/mail/u/0/#drafts"}
          target="_blank"
          rel="noopener noreferrer"
          data-testid={`${testid}-open`}
          className="inline-flex items-center gap-1.5 h-8 px-3 rounded-full border border-[var(--rammy-border)] text-[12px] text-[var(--rammy-text)] hover:border-[var(--rammy-cyan-dark)] transition"
        >
          <L.ExternalLink size={12}/> Edit in Gmail
        </a>
        {sent && (
          <span className="ml-auto inline-flex items-center gap-1 text-[11px] text-emerald-600 font-semibold" data-testid={`${testid}-sent-indicator`}>
            <L.Check size={12}/> Sent
          </span>
        )}
      </div>
    </div>
  );
}


function RegenerateMenu({ onPick, testidBase }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  useEffect(() => {
    if (!open) return undefined;
    const onDoc = (e) => { if (!ref.current?.contains(e.target)) setOpen(false); };
    const onKey = (e) => { if (e.key === "Escape") setOpen(false); };
    document.addEventListener("mousedown", onDoc);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDoc);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);
  return (
    <div className="relative" ref={ref}>
      <button
        type="button"
        onClick={() => setOpen(o => !o)}
        className="inline-flex items-center gap-1 rounded-full border border-[var(--rammy-border)] bg-[var(--rm-surface)] text-[var(--rammy-text-muted)] hover:text-[var(--rammy-cyan-dark)] hover:border-[var(--rammy-cyan-dark)]/40 text-[11px] px-2.5 py-1 transition"
        aria-haspopup="menu"
        aria-expanded={open}
        title="Try again with a different style"
        data-testid={testidBase}
      >
        <L.RefreshCcw size={12}/> Regenerate <L.ChevronDown size={11} className={open ? "rotate-180 transition" : "transition"}/>
      </button>
      {open && (
        <div
          role="menu"
          className="absolute z-30 bottom-full left-0 mb-1.5 w-52 rounded-xl border border-[var(--rammy-border)] bg-[var(--rm-surface)] rammy-shadow p-1"
          data-testid={`${testidBase}-menu`}
        >
          {REGEN_STYLES.map((s) => {
            const Icon = s.icon ? L[s.icon] : L.RefreshCcw;
            return (
              <button
                key={s.label}
                type="button"
                role="menuitem"
                onClick={() => { setOpen(false); onPick(s.key); }}
                className="w-full flex items-center gap-2 px-2.5 py-1.5 rounded-lg text-[12.5px] text-[var(--rammy-text)] hover:bg-[var(--rammy-surface-alt)] transition text-left"
                data-testid={`${testidBase}-item-${s.key || "same"}`}
              >
                <Icon size={13} className="text-[var(--rammy-text-muted)]"/>
                <span>{s.label}</span>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}

export default function MessageBubble({
  m, i, prev, isLastUser, isLastAssistant, busy, sharingIdx,
  onEditImage, onSend, onOpenCanvas,
  onDownloadVideo, onDownloadSound,
  onKeepImageMemory, onShareImageAsCard, onSaveSubject,
  onRegenerate, onEditLastUser,
  onSuggestLanguageAccepted, onSuggestLanguageDismissed,
}) {
  const canVerify =
    m.role === "assistant" &&
    !m.streaming &&
    !m.error &&
    !m.verified &&
    !busy &&
    prev?.role === "user" &&
    (prev.content || "").trim().length > 0 &&
    isUncertainReply(m.content);
  const originalQuestion = prev?.content?.trim() || "";
  const cleaned = cleanContent(m.content);
  const contentGate = m.role === "assistant" ? detectContentGate(m.content) : null;
  const [showSources] = useShowSources();
  const [showConfidence] = useShowConfidence();

  return (
    <div className="group/msg flex justify-start">
      {/* Edit-last-user pencil — hover-only, only on the trailing user msg.
          Sits to the LEFT of the pill so the pill itself stays uncluttered. */}
      {m.role === "user" && isLastUser && onEditLastUser && !busy && (
        <button
          type="button"
          onClick={onEditLastUser}
          className="self-center mr-2 w-7 h-7 rounded-full bg-[var(--rm-surface)] border border-[var(--rammy-border)] text-[var(--rammy-text-muted)] hover:text-[var(--rammy-cyan-dark)] hover:border-[var(--rammy-cyan-dark)]/40 flex items-center justify-center opacity-0 group-hover/msg:opacity-100 focus:opacity-100 transition"
          aria-label="Edit last message"
          title="Edit last message"
          data-testid={`chat-edit-last-user-${i}`}
        >
          <L.Pencil size={12}/>
        </button>
      )}
      <div className="flex flex-col items-start gap-1.5 w-full max-w-[68ch]">
        <div
          className={
            m.role === "user"
              ? "rm-turn-user whitespace-pre-wrap"
              // Assistant: warm paper, serif, a 2px indigo rule on the left.
              // No bubble — the typeface tells you who's talking.
              : "rammy-response rm-placed w-full"
          }
          data-testid={`chat-msg-${m.role}`}
        >
          {m.role !== "user" && (
            <div
              className="rammy-response__marker rammy-orb rammy-orb--xs"
              data-mood={m.streaming ? "thinking" : "idle"}
              aria-hidden
            />
          )}
          {/* Attachments the user sent */}
          {m.images && m.images.length > 0 && (
            <div className="flex flex-wrap gap-2 mb-2" data-testid="chat-msg-images">
              {m.images.map((img, j) => {
                const src = typeof img === "string" ? img : img.src;
                const dataUrl = typeof img === "string" ? null : img.dataUrl;
                return (
                  <div key={j} className="relative group">
                    <img src={src} alt="" className="max-h-40 rounded-xl border border-black/10 object-cover"/>
                    {dataUrl && onEditImage && (
                      <button
                        type="button"
                        onClick={() => onEditImage({ dataUrl })}
                        className="absolute bottom-1.5 right-1.5 inline-flex items-center gap-1 rounded-full bg-[var(--rm-ink-900)]/85 text-white text-[10px] px-2 py-1 opacity-0 group-hover:opacity-100 transition"
                        data-testid={`chat-edit-user-image-${i}-${j}`}
                        title="Edit with words"
                      >
                        <L.Wand2 size={10}/> Edit
                      </button>
                    )}
                  </div>
                );
              })}
            </div>
          )}
          {m.docs && m.docs.length > 0 && (
            <div className="flex flex-wrap gap-2 mb-2" data-testid="chat-msg-docs">
              {m.docs.map((d, j) => (
                <div
                  key={j}
                  className="inline-flex items-center gap-2 rounded-xl bg-white/15 border border-white/25 px-2.5 py-1.5"
                  data-testid={`chat-msg-doc-${i}-${j}`}
                  title={d.name}
                >
                  <L.FileText size={13}/>
                  <span className="text-[11px] font-medium max-w-[180px] truncate">{d.name}</span>
                  <span className="text-[9px] uppercase tracking-widest opacity-70">{d.kind}</span>
                </div>
              ))}
            </div>
          )}

          {/* Generated image */}
          {m.imageDataUrl && (
            <img
              src={m.imageDataUrl}
              alt={m.imagePrompt || "Generated image"}
              className="mt-1 mb-2 rounded-2xl border border-black/10 max-w-full"
              data-testid={`chat-generated-image-${i}`}
            />
          )}
          {m.imageDataUrl && (m.imageModelLabel || m.castUsed?.length) && (
            <div
              className="mb-2 -mt-1 flex items-center gap-2 flex-wrap text-[10px] uppercase tracking-widest text-[var(--rammy-text-muted)]"
              data-testid={`chat-image-model-${i}`}
            >
              {m.imageModelLabel && <span>Made with {m.imageModelLabel}</span>}
              {m.castUsed?.length > 0 && (
                <span
                  className="normal-case tracking-normal text-[11px] text-[var(--rammy-cyan-dark)]"
                  data-testid={`chat-image-cast-${i}`}
                >
                  with {m.castUsed.map(c => c.name).join(" and ")}
                </span>
              )}
            </div>
          )}
          {m.imageDataUrl && onSaveSubject && (
            <button
              onClick={() => onSaveSubject(m.imageDataUrl)}
              className={`mb-2 inline-flex items-center gap-1.5 rounded-full border px-3 py-1 text-[11px] transition-colors ${
                m.castHint
                  ? "border-[var(--rammy-cyan)] bg-[var(--rammy-cyan)]/10 text-[var(--rammy-text)]"
                  : "border-[var(--rammy-border)] text-[var(--rammy-text-sec)] hover:border-[var(--rammy-cyan)] hover:text-[var(--rammy-text)]"
              }`}
              data-testid={`chat-save-subject-${i}`}
            >
              <L.UserPlus size={12}/>
              {m.castHint
                ? "Want me to remember them for next time?"
                : m.castUsed?.length ? "Save another subject" : "Save as subject"}
            </button>
          )}
          {m.imageFallbackReason && (
            <div
              className="mb-2 -mt-1 inline-flex items-center gap-1.5 rounded-full bg-amber-50 border border-amber-200 text-amber-800 px-2.5 py-1 text-[11px]"
              data-testid={`chat-image-fallback-note-${i}`}
            >
              <L.Info size={12}/>
              <span>{m.imageFallbackReason}</span>
            </div>
          )}
          {m.imageLoading && (
            <div
              className="mt-1 mb-2 w-64 h-64 rounded-2xl border border-dashed border-[var(--rammy-cyan)]/40 bg-[var(--rammy-cyan)]/5 flex flex-col items-center justify-center text-[var(--rammy-cyan-dark)]"
              data-testid={`chat-generating-${i}`}
            >
              <L.Loader2 size={22} className="animate-spin"/>
              <div className="mt-3 text-[11px] uppercase tracking-widest font-semibold">Sketching…</div>
              <div className="mt-1 text-[11px] text-[var(--rammy-text-muted)]">~15 sec</div>
            </div>
          )}

          {/* Generated video */}
          {m.videoDataUrl && (
            <video
              src={m.videoDataUrl}
              controls autoPlay muted loop playsInline
              className="mt-1 mb-2 rounded-2xl border border-black/10 max-w-full max-h-96 bg-black"
              data-testid={`chat-generated-video-${i}`}
            />
          )}
          {m.videoLoading && (
            <VideoWaiting
              testid={`chat-generating-video-${i}`}
              prompt={m.videoPrompt}
              duration={m.videoDuration}
              size={m.videoSize}
              startedAt={m.videoStartedAt}
            />
          )}

          {/* Generated sound */}
          {m.soundDataUrl && (
            <div className="mt-1 mb-2 rounded-2xl bg-[var(--rammy-cyan)]/8 border border-[var(--rammy-cyan)]/25 p-3 w-80" data-testid={`chat-generated-sound-${i}`}>
              <div className="flex items-center gap-2 text-[10px] uppercase tracking-widest font-semibold text-[var(--rammy-cyan-dark)] mb-2">
                <L.Music size={11}/> {m.soundLoop ? "Ambience" : "Sound"} · {(m.soundDuration || 0).toFixed(1)}s
              </div>
              <audio src={m.soundDataUrl} controls loop={!!m.soundLoop} className="w-full" data-testid={`chat-sound-audio-${i}`}/>
            </div>
          )}
          {m.soundLoading && (
            <div className="mt-1 mb-2 w-80 rounded-2xl border border-dashed border-[var(--rammy-cyan)]/40 bg-[var(--rammy-cyan)]/5 p-4" data-testid={`chat-generating-sound-${i}`}>
              <div className="flex items-center gap-2 text-[var(--rammy-cyan-dark)]">
                <L.Music size={16}/>
                <div className="text-[11px] uppercase tracking-widest font-semibold">Composing…</div>
              </div>
              <div className="mt-2 flex items-center gap-2">
                <L.Loader2 size={16} className="animate-spin text-[var(--rammy-cyan-dark)]"/>
                <div className="text-[13px] text-[var(--rammy-text)] italic">Listening for what you meant.</div>
              </div>
              {m.soundPrompt && (
                <div className="mt-2 text-[11px] text-[var(--rammy-text-muted)] line-clamp-2">&ldquo;{m.soundPrompt}&rdquo;</div>
              )}
            </div>
          )}

          {/* Reminder — inline confirmation card created via <generate kind="reminder"/> */}
          {m.reminderCreated && (
            <ReminderCard reminder={m.reminderCreated} testid={`chat-reminder-${i}`}/>
          )}

          {/* Weather — structured card, not the model retyping numbers. */}
          {m.weather && <WeatherCard card={m.weather} testid={`chat-weather-${i}`}/>}

          {/* Calendar event — Rammy booked real time, with a one-hour Undo. */}
          {m.calendarEvent && (
            <CalendarEventCard event={m.calendarEvent} testid={`chat-calendar-event-${i}`}/>
          )}

          {/* Promise — recorded in the commitment store via <generate kind="commitment"/> */}
          {m.commitment && (
            <PromiseCard promise={m.commitment} testid={`chat-promise-${i}`}/>
          )}

          {/* Gmail draft — inline confirmation card created via
              <generate kind="gmail-draft"/>. User can review + send from here. */}
          {m.gmailDraft && (
            <GmailDraftCard draft={m.gmailDraft} testid={`chat-gmail-draft-${i}`}/>
          )}

          {/* Body text — verbatim for user, segmented markdown + inline
              artifact cards for assistant. Fenced code blocks that represent
              a full artifact (a website, an illustration, a doc) are HIDDEN
              from the transcript and replaced with a warm preview card so
              non-technical users never see raw HTML streaming past them. */}
          {(() => {
            if (m.role === "user") return cleaned;
            const mediaBusy = m.imageLoading || m.videoLoading || m.soundLoading;
            if (!cleaned && !m.streaming) return "";
            if (!cleaned && mediaBusy) return "";

            const segments = splitByArtifacts(cleaned, { streaming: !!m.streaming });
            const lastTextIdx = (() => {
              for (let k = segments.length - 1; k >= 0; k--) {
                if (segments[k].kind === "text") return k;
              }
              return -1;
            })();
            const hasArtifactSegment = segments.some(
              s => s.kind === "artifact" || s.kind === "artifact-building"
            );

            // If nothing has streamed yet, show the "thinking…" pulse — or,
            // when we've received a research/progress narration from the
            // backend, use THAT as the placeholder so the wait is
            // contextual ("Checked the web · composing your reply…") rather
            // than generic. The progressLabel takes priority over "checking"
            // for verified replies too.
            if (!segments.length && m.streaming) {
              const placeholder = m.progressLabel || (m.verified ? "checking…" : "thinking…");
              return (
                <>
                  <LiveActivityStrip
                    label={m.progressLabel}
                    sources={m.progressSources}
                    testid={`chat-live-activity-${i}`}
                  />
                  <AssistantBody
                    text=""
                    streaming
                    placeholder={m.progressSources?.length ? "composing your reply…" : placeholder}
                  />
                </>
              );
            }

            return (
              <>
                {m.streaming && (
                  <LiveActivityStrip
                    label={m.progressLabel}
                    sources={m.progressSources}
                    testid={`chat-live-activity-${i}`}
                  />
                )}
                {contentGate && (
                  <div
                    className="mb-2.5 rounded-xl border-l-4 border-rose-500 bg-rose-50 px-3 py-2.5"
                    role="alert"
                    data-testid={`chat-msg-legal-gate-${i}`}
                  >
                    <div className="text-[10px] uppercase tracking-widest font-semibold text-rose-700 flex items-center gap-1">
                      <span aria-hidden>⚠</span> Not legal advice
                    </div>
                    <div className="text-[12px] text-rose-900 leading-snug mt-1">
                      {contentGate.reason} Get this reviewed by a licensed
                      professional in {contentGate.jurisdiction} before use.
                    </div>
                  </div>
                )}
                {segments.map((seg, k) => {
                  if (seg.kind === "text") {
                    // Only the trailing text segment carries the caret so a
                    // paragraph BEFORE the artifact doesn't get a caret while
                    // the artifact is still building below it.
                    const isLastText = k === lastTextIdx;
                    const streamingHere = !!m.streaming && isLastText && !hasArtifactSegment;
                    return (
                      <AssistantBody
                        key={`t-${k}`}
                        text={seg.text}
                        streaming={streamingHere}
                        placeholder={m.verified ? "checking…" : "thinking…"}
                      />
                    );
                  }
                  if (seg.kind === "artifact-building") {
                    return (
                      <ArtifactBuildingCard
                        key={`b-${k}`}
                        lang={seg.lang}
                        testid={`chat-artifact-building-${i}`}
                        onRetry={onRegenerate ? () => onRegenerate(i) : undefined}
                        progressLabel={m.progressLabel}
                      />
                    );
                  }
                  if (seg.kind === "artifact-interrupted") {
                    return (
                      <ArtifactInterruptedCard
                        key={`x-${k}`}
                        lang={seg.lang}
                        testid={`chat-artifact-interrupted-${i}`}
                        onRetry={onRegenerate ? () => onRegenerate(i) : undefined}
                      />
                    );
                  }
                  // seg.kind === "artifact"
                  const art = seg.artifact;
                  return (
                    <ArtifactReadyCard
                      key={`a-${k}`}
                      artifact={art}
                      testid={`chat-artifact-ready-${i}-${k}`}
                      onOpen={() => onOpenCanvas?.({ ...art, id: `msg${i}-${k}` })}
                    />
                  );
                })}
              </>
            );
          })()}

          {/* Compute results (chart/table/code) */}
          {Array.isArray(m.computes) && m.computes.length > 0 && (
            <div className="mt-3 space-y-2" data-testid={`chat-msg-computes-${i}`}>
              {m.computes.map((c, k) => (
                <div
                  key={k}
                  className={`rounded-xl border px-3 py-2 text-[12px] ${
                    c.ok
                      ? "bg-[var(--rammy-cyan)]/8 border-[var(--rammy-cyan)]/30 text-[var(--rammy-text)]"
                      : "bg-red-50 border-red-200 text-red-700"
                  }`}
                  data-testid={`chat-compute-${i}-${k}`}
                >
                  <div className="flex items-center gap-1.5 text-[9px] uppercase tracking-widest font-semibold opacity-70 mb-1">
                    <L.Terminal size={10}/>
                    {c.ok ? (c.chart_png_b64 ? "Chart" : "Computed") : "Compute error"}
                  </div>
                  {c.ok ? (
                    <>
                      {c.chart_png_b64 && (
                        <img
                          src={`data:image/png;base64,${c.chart_png_b64}`}
                          alt="Chart"
                          className="rounded-lg max-w-full bg-[var(--rammy-surface-alt)]/50"
                          data-testid={`chat-compute-chart-${i}-${k}`}
                        />
                      )}
                      {c.text && !c.chart_png_b64 && (
                        <pre className="text-[11.5px] font-mono whitespace-pre overflow-x-auto leading-snug">{c.text}</pre>
                      )}
                      {c.chart_png_b64 && (
                        <div className="mt-2 flex items-center">
                          <button
                            type="button"
                            onClick={() => {
                              const a = document.createElement("a");
                              a.href = `data:image/png;base64,${c.chart_png_b64}`;
                              const stamp = new Date().toISOString().replace(/[:.]/g, "-").slice(0, 19);
                              a.download = `rammy-chart-${stamp}.png`;
                              document.body.appendChild(a); a.click(); a.remove();
                              toast.success("Chart saved");
                            }}
                            className="text-[11px] inline-flex items-center gap-1 text-[var(--rammy-cyan-dark)] hover:underline"
                            data-testid={`chat-compute-chart-save-${i}-${k}`}
                          >
                            <L.Download size={11}/> Save chart
                          </button>
                        </div>
                      )}
                    </>
                  ) : (
                    <div className="text-[12px]">{c.error}</div>
                  )}
                  <details className="mt-1 opacity-60">
                    <summary className="cursor-pointer text-[10px] uppercase tracking-widest">Code</summary>
                    <pre className="text-[10.5px] font-mono whitespace-pre overflow-x-auto leading-snug mt-1">{c.code}</pre>
                  </details>
                </div>
              ))}
            </div>
          )}

          {/* Streaming caret is owned by AssistantBody's StreamingCaret on
              the trailing text segment. The old top-level caret has been
              removed to avoid a stray blinker next to an ArtifactBuildingCard
              when the streaming body is now hidden inside the artifact. */}
        </div>

        {/* Post-bubble action rows */}
        {m.imageDataUrl && !m.kept && (
          <div className="flex items-center gap-3 pl-1 flex-wrap">
            {onKeepImageMemory && (
              <button
                type="button"
                onClick={() => onKeepImageMemory(m)}
                className="text-[11px] inline-flex items-center gap-1 text-[var(--rammy-cyan-dark)] hover:underline"
                data-testid={`chat-keep-memory-${i}`}
              >
                <L.BookmarkPlus size={11}/> Keep this memory
              </button>
            )}
            {onEditImage && (
              <button
                type="button"
                onClick={() => onEditImage({ dataUrl: m.imageDataUrl, sourcePrompt: m.imagePrompt })}
                className="text-[11px] inline-flex items-center gap-1 text-[var(--rammy-text-sec)] hover:text-[var(--rammy-cyan-dark)] hover:underline"
                data-testid={`chat-edit-generated-${i}`}
              >
                <L.Wand2 size={11}/> Edit with words
              </button>
            )}
            {onShareImageAsCard && (
              <button
                type="button"
                onClick={() => onShareImageAsCard(m, i)}
                disabled={sharingIdx === i}
                className="text-[11px] inline-flex items-center gap-1 text-[var(--rammy-text-sec)] hover:text-[var(--rammy-cyan-dark)] hover:underline disabled:opacity-60"
                data-testid={`chat-share-card-${i}`}
              >
                {sharingIdx === i
                  ? <><L.Loader2 size={11} className="animate-spin"/> Preparing…</>
                  : <><L.Share2 size={11}/> Share as card</>}
              </button>
            )}
          </div>
        )}
        {m.imageDataUrl && m.kept && (
          <div className="flex items-center gap-3 pl-1 flex-wrap">
            <div className="text-[11px] inline-flex items-center gap-1 text-[var(--rammy-text-muted)]" data-testid={`chat-kept-${i}`}>
              <L.Check size={11}/> Kept in your Memory Garden
            </div>
            {onEditImage && (
              <button
                type="button"
                onClick={() => onEditImage({ dataUrl: m.imageDataUrl, sourcePrompt: m.imagePrompt })}
                className="text-[11px] inline-flex items-center gap-1 text-[var(--rammy-text-sec)] hover:text-[var(--rammy-cyan-dark)] hover:underline"
                data-testid={`chat-edit-generated-kept-${i}`}
              >
                <L.Wand2 size={11}/> Edit with words
              </button>
            )}
            {onShareImageAsCard && (
              <button
                type="button"
                onClick={() => onShareImageAsCard(m, i)}
                disabled={sharingIdx === i}
                className="text-[11px] inline-flex items-center gap-1 text-[var(--rammy-text-sec)] hover:text-[var(--rammy-cyan-dark)] hover:underline disabled:opacity-60"
                data-testid={`chat-share-card-kept-${i}`}
              >
                {sharingIdx === i
                  ? <><L.Loader2 size={11} className="animate-spin"/> Preparing…</>
                  : <><L.Share2 size={11}/> Share as card</>}
              </button>
            )}
          </div>
        )}
        {m.videoDataUrl && (
          <div className="flex items-center gap-3 pl-1 flex-wrap">
            {onDownloadVideo && (
              <button
                type="button"
                onClick={() => onDownloadVideo(m, i)}
                className="text-[11px] inline-flex items-center gap-1 text-[var(--rammy-cyan-dark)] hover:underline"
                data-testid={`chat-download-video-${i}`}
              >
                <L.Download size={11}/> Save video
              </button>
            )}
            <div className="text-[11px] inline-flex items-center gap-1 text-[var(--rammy-text-muted)]">
              <L.Film size={11}/> {m.videoDuration || 4}s · {m.videoSize || "1280x720"}
            </div>
          </div>
        )}
        {m.soundDataUrl && (
          <div className="flex items-center gap-3 pl-1 flex-wrap">
            {onDownloadSound && (
              <button
                type="button"
                onClick={() => onDownloadSound(m)}
                className="text-[11px] inline-flex items-center gap-1 text-[var(--rammy-cyan-dark)] hover:underline"
                data-testid={`chat-download-sound-${i}`}
              >
                <L.Download size={11}/> Save audio
              </button>
            )}
            {m.soundLoop && (
              <div className="text-[11px] inline-flex items-center gap-1 text-[var(--rammy-text-muted)]">
                <L.Repeat size={11}/> Looping
              </div>
            )}
          </div>
        )}

        {/* Language auto-detect chip — appears when the user typed/spoke
            in a language different from their profile setting. One-tap
            switch that PATCH-es /me/settings and refreshes the profile
            downstream; dismissal is sticky per code. */}
        {m.role === "assistant" && !m.streaming && m.trace?.suggest_language && (
          <LanguageSuggestionChip
            code={m.trace.suggest_language.code}
            name={m.trace.suggest_language.name}
            onSwitched={() => {
              // Hide the chip immediately for this bubble; a full profile
              // refresh happens via the parent's user-refetch which also
              // updates future system prompts.
              onSuggestLanguageAccepted?.(m.trace.suggest_language.code);
            }}
            onDismissed={() => {
              onSuggestLanguageDismissed?.(m.trace.suggest_language.code);
            }}
            testid={`chat-lang-suggest-${i}`}
          />
        )}

        {/* Sources + verify chip — off by default. Toggle in Settings ›
            Chat interface › "Show source links in chat". The compact
            SourceBadge below stays visible either way. */}
        {showSources && Array.isArray(m.sources) && m.sources.length > 0 && (m.verified || (m.trace?.live_sources?.length)) && (
          <div className="flex flex-wrap gap-1.5 pl-1" data-testid={`chat-sources-${i}`}>
            <span className="text-[10px] uppercase tracking-widest text-[var(--rammy-text-muted)] font-semibold self-center">Sources</span>
            {m.sources.map((s, k) => (
              <a
                key={k}
                href={s.url || "#"}
                target="_blank"
                rel="noreferrer noopener"
                className="text-[11px] px-2 py-0.5 rounded-full bg-[var(--rm-surface)] border border-[var(--rammy-cyan)]/40 text-[var(--rammy-cyan-dark)] hover:bg-[var(--rammy-cyan)]/10 transition inline-flex items-center gap-1"
                data-testid={`chat-source-link-${i}-${k}`}
              >
                <L.ExternalLink size={10}/>{s.title}
              </a>
            ))}
          </div>
        )}
        {/* Where a factual answer came from. Provenance only — no confidence
            wording or scores (29 Aug 2026): a companion that says it has "low
            confidence" in itself just makes people trust it less. Shows only
            when the backend judged the lookup worth citing. */}
        {m.role === "assistant" && !m.streaming && showConfidence &&
         Array.isArray(m.sources) && m.sources.length > 0 && (
          <div className="pl-1">
            <SourceBadge credibility={m.credibility} sources={m.sources} query={originalQuestion}/>
          </div>
        )}
        {canVerify && onSend && (
          <button
            type="button"
            onClick={() => onSend(originalQuestion, { verify: true })}
            className="text-[11px] pl-1 inline-flex items-center gap-1 text-[var(--rammy-cyan-dark)] hover:underline"
            data-testid={`chat-verify-btn-${i}`}
            title="Let me look this up for you"
          >
            <L.Search size={11}/> Let me check that for you
          </button>
        )}

        {/* Canvas artifact chip is intentionally NOT rendered here anymore.
            The inline ArtifactReadyCard (rendered in the message body) is the
            single UI surface for opening a generated artifact in Canvas, so
            the raw fenced ```html``` never leaks into the transcript and the
            user gets one clear Preview action instead of two duplicated
            entry points. */}

        {/* Post-reply actions — feedback, copy, regenerate. The old
            RammyBrainStrip (which surfaced "15 memories · weather" etc.
            beneath every reply) has been removed at user request — the
            transparency data is still on `m.trace` for anyone who wants
            to expose it later, but it was distracting attention away
            from the actual answer. */}
        {m.role === "assistant" && !m.streaming && m.trace && (
          <>
            <div className="flex items-center gap-2 mt-1 flex-wrap">
              <CopyReplyButton text={cleaned}/>
              <SendToStudioButton text={cleaned} testid={`chat-to-studio-${i}`}/>
              {isLastAssistant && onRegenerate && !busy && (
                <RegenerateMenu
                  testidBase={`chat-regenerate-${i}`}
                  onPick={(styleKey) => onRegenerate(styleKey)}
                />
              )}
              {m.trace.assistant_message_id && (
                <MessageFeedback messageId={m.trace.assistant_message_id}/>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
}
